import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AuthContext from '../context/AuthContext';
import { getActiveGymMemberships, verifyGymMembershipCode } from '../api';

const formatEuros = (amount) => new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
}).format(Number(amount || 0) / 100);

const formatDate = (value) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
};

const memberName = (membership) => {
  const fullName = [membership?.member?.first_name, membership?.member?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || 'Client Gyma';
};

const StatCard = ({ icon, label, value, color }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      borderRadius: 3,
      borderColor: `${color}35`,
      bgcolor: `${color}08`,
    }}
  >
    <CardContent>
      <Box display="flex" alignItems="center" gap={1.5}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            bgcolor: color,
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={900}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ActiveMembershipsPanel = ({
  title = 'Abonnements clients actifs',
  showVerifier = true,
}) => {
  const { authTokens } = useContext(AuthContext);
  const [memberships, setMemberships] = useState([]);
  const [summary, setSummary] = useState({
    total_active: 0,
    expiring_in_7_days: 0,
    total_revenue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [verification, setVerification] = useState(null);

  const loadMemberships = async () => {
    if (!authTokens?.access) return;
    setLoading(true);
    setError('');
    try {
      const data = await getActiveGymMemberships(authTokens.access);
      setMemberships(Array.isArray(data?.memberships) ? data.memberships : []);
      setSummary(data?.summary || {});
    } catch (requestError) {
      setError(requestError.message || 'Impossible de charger les abonnements actifs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMemberships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authTokens?.access]);

  const filteredMemberships = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('fr-FR');
    if (!query) return memberships;
    return memberships.filter((membership) => [
      memberName(membership),
      membership.member?.email,
      membership.member?.phone_number,
      membership.plan_name,
      membership.confirmation_code,
    ].some((value) => String(value || '').toLocaleLowerCase('fr-FR').includes(query)));
  }, [memberships, search]);

  const verifyCode = async () => {
    if (!/^\d{6}$/.test(accessCode)) {
      setVerification({ valid: false, reason: "Saisissez un code d'accès à 6 chiffres." });
      return;
    }
    setChecking(true);
    setVerification(null);
    try {
      setVerification(await verifyGymMembershipCode(accessCode, authTokens.access));
    } catch (requestError) {
      setVerification({ valid: false, reason: requestError.message });
    } finally {
      setChecking(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        gap={2}
        minWidth={0}
      >
        <Box>
          <Typography variant="h5" fontWeight={900}>{title}</Typography>
          <Typography color="text.secondary">
            Seuls les achats confirmés par Stripe et encore valides apparaissent ici.
          </Typography>
        </Box>
        <Tooltip title="Actualiser">
          <Button
            variant="outlined"
            onClick={loadMemberships}
            disabled={loading}
            startIcon={<RefreshRoundedIcon />}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
          >
            Actualiser
          </Button>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' },
          gap: 2,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
          <StatCard
            icon={<VerifiedUserRoundedIcon />}
            label="Abonnements actifs"
            value={summary.total_active || 0}
            color="#000449"
          />
          <StatCard
            icon={<WarningAmberRoundedIcon />}
            label="Expirent sous 7 jours"
            value={summary.expiring_in_7_days || 0}
            color="#F95A00"
          />
          <StatCard
            icon={<PaymentsRoundedIcon />}
            label="Valeur des abonnements actifs"
            value={formatEuros(summary.total_revenue || 0)}
            color="#16866B"
          />
      </Box>

      {showVerifier && (
        <Card
          sx={{
            borderRadius: 4,
            color: '#fff',
            background: 'linear-gradient(135deg, #000449 0%, #101A63 68%, #F95A00 160%)',
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 5fr) minmax(0, 7fr)' },
                gap: 2,
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              <Box minWidth={0}>
                <Typography variant="h6" fontWeight={900}>Contrôler un accès</Typography>
                <Typography sx={{ opacity: 0.78 }}>
                  Comparez le code présenté par le client avec une transaction Stripe valide.
                </Typography>
              </Box>
              <Box minWidth={0}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    fullWidth
                    value={accessCode}
                    onChange={(event) => {
                      setAccessCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                      setVerification(null);
                    }}
                    onKeyDown={(event) => event.key === 'Enter' && verifyCode()}
                    placeholder="Code à 6 chiffres"
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: 2,
                      '& .MuiOutlinedInput-root': { borderRadius: 2 },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={verifyCode}
                    disabled={checking}
                    sx={{ bgcolor: '#F95A00', minWidth: 150, borderRadius: 2 }}
                  >
                    {checking ? <CircularProgress size={22} color="inherit" /> : 'Vérifier'}
                  </Button>
                </Stack>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {verification && (
        <Alert
          severity={verification.valid ? 'success' : 'error'}
          icon={verification.valid ? <CheckCircleRoundedIcon /> : undefined}
          sx={{ borderRadius: 3 }}
        >
          <Typography fontWeight={800}>{verification.reason}</Typography>
          {verification.membership && (
            <Typography variant="body2">
              {memberName(verification.membership)} · {verification.membership.plan_name} · valide jusqu'au{' '}
              {formatDate(verification.membership.expires_at)}
            </Typography>
          )}
        </Alert>
      )}

      <Card
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box', p: { xs: 2, md: 3 } }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un client, un abonnement ou un code"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? (
            <Box py={6} textAlign="center"><CircularProgress /></Box>
          ) : filteredMemberships.length ? (
            <>
            <TableContainer
              sx={{
                display: { xs: 'none', md: 'block' },
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflowX: 'auto',
                overscrollBehaviorX: 'contain',
              }}
            >
              <Table size="small" sx={{ width: '100%', minWidth: 760, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '25%' }}>Client</TableCell>
                    <TableCell sx={{ width: '19%' }}>Abonnement</TableCell>
                    <TableCell sx={{ width: '17%' }}>Validité</TableCell>
                    <TableCell sx={{ width: '18%' }}>Code d'accès</TableCell>
                    <TableCell sx={{ width: '21%' }}>Paiement</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMemberships.map((membership) => (
                    <TableRow key={membership.id} hover>
                      <TableCell sx={{ overflow: 'hidden' }}>
                        <Typography fontWeight={800}>{memberName(membership)}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                          {membership.member?.email || membership.member?.phone_number || membership.member?.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={700}>{membership.plan_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatEuros(membership.final_price)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{formatDate(membership.expires_at)}</Typography>
                        <Chip
                          size="small"
                          color={membership.days_left <= 7 ? 'warning' : 'success'}
                          label={`${membership.days_left} jour${membership.days_left > 1 ? 's' : ''}`}
                          sx={{ mt: 0.5 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={membership.confirmation_code}
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '1rem',
                            fontWeight: 900,
                            letterSpacing: '0.12em',
                            bgcolor: '#000449',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Référence : ${membership.payment_reference}`}>
                          <Chip
                            size="small"
                            icon={<CheckCircleRoundedIcon />}
                            color="success"
                            label="Stripe confirmé"
                            sx={{ maxWidth: '100%' }}
                          />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
              {filteredMemberships.map((membership) => (
                <Card key={`mobile-${membership.id}`} variant="outlined" sx={{ borderRadius: 3, borderColor: 'rgba(0,4,73,.12)' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack spacing={1.35}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={900}>{memberName(membership)}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                            {membership.member?.email || membership.member?.phone_number || membership.member?.id}
                          </Typography>
                        </Box>
                        <Chip size="small" color="success" icon={<CheckCircleRoundedIcon />} label="Payé" />
                      </Stack>
                      <Box display="flex" justifyContent="space-between" gap={1}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Abonnement</Typography>
                          <Typography fontWeight={800}>{membership.plan_name}</Typography>
                          <Typography variant="caption">{formatEuros(membership.final_price)}</Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="caption" color="text.secondary">Valide jusqu’au</Typography>
                          <Typography fontWeight={750}>{formatDate(membership.expires_at)}</Typography>
                          <Chip
                            size="small"
                            color={membership.days_left <= 7 ? 'warning' : 'success'}
                            label={`${membership.days_left} jour${membership.days_left > 1 ? 's' : ''}`}
                            sx={{ mt: .5 }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ p: 1.25, borderRadius: 2.5, textAlign: 'center', bgcolor: '#EEF1FF' }}>
                        <Typography variant="caption" color="text.secondary">Code d’accès</Typography>
                        <Typography sx={{ color: '#000449', fontFamily: 'monospace', fontSize: '1.25rem', fontWeight: 950, letterSpacing: '.15em' }}>
                          {membership.confirmation_code}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
            </>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Aucun abonnement actif ne correspond à cette recherche.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ActiveMembershipsPanel;
